import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Skeleton, Progress, Tooltip } from "antd";
import ReactHighcharts from "react-highcharts";
import { formatAmount } from "../../constants/overview";
import { RoundedSelect } from "../tailwind/atoms";
import OpdIcon from "./OpdIcon";
import { DS_CHART_COLORS, DS_CHART_TOKENS, getDsChartColor, normalizeHighchartsConfig } from "../../utils/chartTheme";

const defaultColors = DS_CHART_COLORS;

const MAX_VISIBLE_ITEMS = 8;

const CommonSplitPlot = ({ data, loading = false, title = "", shareLabel ='Share', hideImage = false, servicesMap = {}, currentService, handleServiceChange = () => {}, initialMetric = "amount", hideToggle = false, hideSummary = false, hideAov = false, precision = 1, hideBrackets = false }) => {
  const [metric, setMetric] = useState(initialMetric);
  const [donutOptions, setDonutOptions] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const listWrapperRef = useRef(null);
  const [listMaxHeight, setListMaxHeight] = useState("0px");

  const numberFormatter = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

  useEffect(() => {
    if (!data) return;
    const categories = Object.keys(data);
    const total = categories.reduce(
      (sum, cat) => sum + Number(data[cat][metric] || 0),
      0
    );

    const chartData = categories.map((cat, idx) => {
      const val = Number(data[cat][metric] || 0);
      return {
        name: cat,
        y: val,
        color: getDsChartColor(defaultColors[idx % defaultColors.length]),
        percentage: total ? ((val / total) * 100).toFixed(precision) : "0.00",
      };
    });

    setDonutOptions({
      chart: { type: "pie", spacingTop: 0 },
      title: { text: "" },
      credits: { enabled: false },
      tooltip: {
        pointFormatter: function () {
          const val =
            metric === "amount"
              ? `₹${numberFormatter.format(this.y)}`
              : numberFormatter.format(this.y);
          const percentStr = ` (${this.options.percentage}%)`
          return `<b>${this.name}:</b> ${val}${percentStr}`;
        },
      },
      plotOptions: {
        pie: {
          innerSize: "60%",
          dataLabels: {
            enabled: true,
            formatter: function () {
              const val =
                metric === "amount"
                  ? `₹${formatAmount(this.y)}`
                  : numberFormatter.format(this.y);
              if (hideBrackets) return `${this.point.name}: ${this.point.options.percentage}%`;
              return `${this.point.name}: ${val}`;
            },
            style: { fontSize: "11px", fontFamily: "JetBrains Mono", color: DS_CHART_TOKENS.gray700 },
          },
        },
      },
      series: [{ name: metric, data: chartData }],
    });
  }, [metric, data]);

  // Adjust the wrapper maxHeight when expanded state or data changes
  useEffect(() => {
    const wrapper = listWrapperRef.current;
    if (!wrapper) return;

    // If expanding, set to scrollHeight to show all items
    if (expanded) {
      // allow browser to compute scrollHeight
      const fullHeight = wrapper.scrollHeight;
      setListMaxHeight(`${fullHeight}px`);
      // after animation, clear maxHeight to allow natural height (prevents clipping on resize)
      const t = setTimeout(() => setListMaxHeight("none"), 350);
      return () => clearTimeout(t);
    }

    // If collapsing, set to fixed height for MAX_VISIBLE_ITEMS rows
    // Estimate row height from first child (falls back to 56px)
    const firstChild = wrapper.querySelector(".opd-row");
    const rowHeight = firstChild ? firstChild.getBoundingClientRect().height : 56;
    const collapseHeight = Math.ceil(rowHeight * MAX_VISIBLE_ITEMS);
    // Set to pixel height to animate from current height
    setListMaxHeight(`${collapseHeight}px`);
  }, [expanded, data]);

  if (loading) {
    return (
      <Card
        className="border border-gray-300 shadow-sm"
        style={{ width: "100%", borderRadius: 14, overflow: "hidden" }}
        bodyStyle={{ borderRadius: 14 }}
      >
        <h1 className="mb-4 flex items-center gap-4 text-base leading-none text-gray-900">
          {hideImage ? null : <OpdIcon name="split" className="h-6 w-6 rounded border border-blue-200 bg-blue-100 p-1.5 text-blue-700" />}
          <span className="font-fig-semibold">{title}</span>
        </h1>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  if (!data) {
    return (
      <Card
        className="border border-gray-300 shadow-sm"
        title={title}
        style={{ width: "100%", borderRadius: 14, overflow: "hidden" }}
        bodyStyle={{ borderRadius: 14 }}
      >
        <p>No data available</p>
      </Card>
    );
  }

  const categories = Object.keys(data);
  const total = categories.reduce(
    (sum, cat) => sum + Number(data[cat][metric] || 0),
    0
  );

  const totalAmount = categories.reduce(
    (sum, cat) => sum + Number(data[cat]["amount"] || 0),
    0
  );
  const totalVolume = categories.reduce(
    (sum, cat) => sum + Number(data[cat]["volume"] || 0),
    0
  );

  return (
    <Card
      className="border border-gray-300 shadow-sm"
      style={{ borderRadius: 14, overflow: "hidden" }}
      bodyStyle={{ borderRadius: 14 }}
    >
      <div className="flex justify-between items-center mb-4">
          <h1 className="flex items-center gap-4 text-base leading-none text-gray-900">
            {hideImage ? null : <OpdIcon name="split" className="h-6 w-6 rounded border border-blue-200 bg-blue-100 p-1.5 text-blue-700" />}
            <span className="font-fig-semibold">{title}</span>
          </h1>

        {!hideToggle && (
          <div className="mb-4 inline-flex space-x-1 overflow-x-auto rounded-md border border-gray-300 bg-gray-200 p-1">
            {["volume", "amount"].map((type) => {
              const isActive = metric === type;
              return (
                <button
                  key={type}
                  onClick={() => setMetric(type)}
                  className={`rounded-sm px-3 py-1.5 text-sm font-fig-semibold transition-colors duration-200 focus:outline-none ${
                    isActive
                      ? "rounded-md bg-white text-gray-900 shadow-sm"
                      : "text-gray-600"
                  }`}
                >
                  {type === "volume" ? "Count" : "Value (₹)"}
                </button>
              );
            })}
          </div>
        )}

        {Object.keys(servicesMap).length > 0 && (
          <RoundedSelect
            listClasses="w-34"
            classes="ml-8 -mt-2"
            selectOptions={Object.values(servicesMap)}
            selectValues={Object.keys(servicesMap).map((_, i) => i)}
            curvalue={servicesMap[currentService] || 'Overall'}
            selectHandler={(index) =>
              handleServiceChange('benefit_split', Object.keys(servicesMap)[index])
            }
          />
        )}
      </div>

      <Row gutter={16}>
        <Col span={12}>
          {donutOptions && <ReactHighcharts config={normalizeHighchartsConfig(donutOptions)} />}
        </Col>

        <Col span={12}>
          {/* Header Row */}
          <div className="flex justify-between items-center mb-2 px-2 sm:px-4">
            <div className="w-1/4" />
            <div className="w-2/4" />
            <div className={`flex justify-end ${hideAov ? 'w-1/12' : 'w-1/4'} gap-6 text-xs sm:text-sm font-fig-normal tracking-wide text-gray-600`}>
              <span>{shareLabel}</span>
              {!hideAov && <span>AOV</span>}
            </div>
          </div>

          {/* List wrapper with transition */}
          <div
            ref={listWrapperRef}
            style={{
              maxHeight: listMaxHeight === "none" ? "none" : listMaxHeight,
              overflow: "hidden",
              transition: "max-height 320ms ease, opacity 200ms ease",
              opacity: listMaxHeight === "none" ? 1 : 1,
            }}
          >
            {categories.map((cat, idx) => {
              const value = Number(data[cat][metric] || 0);
              const aov =
                data[cat].amount && data[cat].volume
                  ? data[cat].amount / (data[cat].volume || 1)
                  : 0;
              const percent = total ? ((value / total) * 100).toFixed(precision) : "0.00";
              const color = defaultColors[idx % defaultColors.length];

              return (
                <div
                  key={cat}
                  className="opd-row my-1 flex flex-col items-center justify-between gap-3 rounded-lg p-2 transition-all hover:bg-gray-50 sm:flex-row sm:gap-4 sm:p-3"
                >
                  {/* Category + Dot */}
                  <div className="flex items-center gap-3 w-full sm:w-1/4">
                    <div
                      className="w-3 h-3 rounded-full shadow-sm flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <Tooltip title={cat}>
                      <span className="truncate text-sm font-fig-normal text-gray-900">
                        {cat}
                      </span>
                    </Tooltip>
                  </div>

                  {/* Progress Bar */}
                  <Tooltip
                    title={`${cat}: ${metric === "amount" ? `₹${numberFormatter.format(value)}` : numberFormatter.format(value)
                      }${hideBrackets ? ` ${percent}%` : ` (${percent}%)`}`}
                    placement="topLeft"
                  >
                    <div className="flex-1 min-w-0">
                      <Progress
                        percent={Number(percent)}
                        showInfo={false}
                        strokeColor={color}
                        trailColor={DS_CHART_TOKENS.gray200}
                        strokeWidth={8}
                        className="w-full cursor-pointer"
                      />
                    </div>
                  </Tooltip>

                  {/* AOV + Share */}
                  <div className={`flex justify-end gap-6 ${hideAov ? 'w-1/12' : 'w-1/4'} text-sm font-jetbrains-mono font-fig-semibold tabular-nums text-gray-900 sm:text-base`}>
                    <span className="w-14 text-right" style={{ color: DS_CHART_TOKENS.semBlue400 }}>{percent}%</span>
                    {!hideAov && (
                      <span className="w-20 text-right text-green-700">
                        ₹{numberFormatter.format(aov)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expand / Collapse Button */}
          {categories.length > MAX_VISIBLE_ITEMS && (
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setExpanded((s) => !s)}
                className="flex items-center gap-2 rounded-full border border-gray-300 px-5 py-2 text-sm font-fig-semibold text-gray-700 shadow-sm transition-all duration-200 hover:bg-gray-100"
                aria-expanded={expanded}
              >
                <span>{expanded ? "Show Less" : `Show More (${categories.length - MAX_VISIBLE_ITEMS})`}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 transform transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          )}

          {/* Totals Summary */}
          {!hideSummary && (
            <div className="mt-4 rounded-lg border border-gray-300 bg-gray-50 p-4 shadow-sm">
              <div className="flex items-center justify-between text-sm sm:text-base">
                <div className="flex flex-col">
                  <span className="font-fig-normal text-gray-600">Total Claims</span>
                  <span className="mt-1 font-jetbrains-mono text-lg font-fig-semibold tabular-nums" style={{ color: DS_CHART_TOKENS.semBlue400 }}>
                    {numberFormatter.format(totalVolume)}
                  </span>
                </div>

                <div className="mx-4 h-10 w-px bg-gray-300 sm:mx-6" />

                <div className="flex flex-col text-right">
                  <span className="font-fig-normal text-gray-600">Total Value</span>
                  <span className="mt-1 font-jetbrains-mono text-lg font-fig-semibold tabular-nums text-green-700">
                    ₹{numberFormatter.format(totalAmount)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default CommonSplitPlot;
